import { userRolesFromId } from '@adamdotai/core/utils'
import { useQuery } from '@urql/next'
import { signOut as nextAuthSignOut, useSession } from 'next-auth/react'
import { useCallback, useMemo } from 'react'
import { graphql } from '@/generated/gql'
import type { UserFilterInput } from '@/generated/gql/graphql'
import { SortEnumType, User_UserStatus } from '@/generated/gql/graphql'
import { clearPreferences } from '@/utils/preference/preference'

const GET_USER_DATA = graphql(`query userData {
    me {
        id
        firstName
        lastName
        displayName
        email
        imageUrl
        role
        createdAt
        isConfirmedEmail
        userStatus
        homePageWidgets
        timezone {
          id
          displayName
        }
        language
        firstDayOfWeek
        is12HoursTimeFormat
        source
        allPermissions
        isMeetplaceChosen
        meetplace {
            id
            name
            meetplaceSite
            cname
            meetplaceUrl
            logoUrl
            fullLogoUrl
            verticalFullLogoUrl
            favIconUrl
            primaryColor
            secondaryColor
            userLimit
            userCount
            pendingNonExpiredUserCount
            groupMembersEditPermissions
            ownerInfo {
              ownerEmail
            }
            calendarPreferences {
              enableTwoWaySync
              allowForwarding
            }
            sso {
              ssoType
              allowedDomains
              ssoURL
              saml {
                ssoCert
                entityId
              }
              oidc {
                clientId
                clientSecret
                tokenUrl
              }
            }
            licenseV1 {
                planId
                planName
                productName
                nextBillDate
                trials
                status
                trialEnd
                cancelAtPeriodEnd
            }
            sso {
              ssoType
              allowedDomains
              ssoURL
              saml {
                ssoCert
                entityId
              }
              oidc {
                clientId
                clientSecret
                tokenUrl
              }
            }
        }
        pendingMeetplaces {
          id
          name
          meetplaceSite
          logoUrl
          userLimit
          userCount
          pendingNonExpiredUserCount
          sso {
            ssoType
            allowedDomains
          }
          ownerInfo {
            ownerFirstname
            ownerLastname
            ownerPhotoUrl
          }
        }
        jobTitle
        ianaTimezoneId
        notificationSettings {
            guestMeetingInvitations
            guestMeetingReminders
            meetingUpdates
            meetingReminders
            actionReminders
            actionUpdates
            actionInteractions
            mentions
            actionWatcherInteractions
            actionWatcherUpdates
        }
        analyticPageWidgets{
          type
          isVisible
        }
        iadamPreferences {
            defaultLanguage
            joinPreference
        }
    }
  }`)

const FILTER_USERS = graphql(`
    query FilterUsers($where: UserFilterInput!, $first: Int, $order:[ UserSortInput!]) {
        users(where: $where, first: $first, order: $order) {
            nodes {
                id
                email
                firstName
                lastName
                imageUrl
                meetplaceId
                userStatus
                role
            }
        }
    }
`)

const GET_USERS = graphql(`query GetUsers($order:[ UserSortInput!]) {
  users(order: $order) {
    nodes {
      id
      email
      firstName
      lastName
      fullName
      imageUrl
      meetplaceId
      role
    }
  }
}`)

const GET_USER = graphql(`query GetUser($userId: UUID!) {
  user(userId: $userId) {
      id
      email
      displayName
      firstName
      lastName
      imageUrl
      meetplaceId
      role
      jobTitle
  }
}
`)

const CREATE_NON_CONFIRMED_USER = graphql(`
    mutation CreateNonConfirmedUser($input: CreateNonConfirmedUserInput!) {
        createNonConfirmedUser(createNonConfirmedUserInput: $input) {
            user {
                id
                fullName
                firstName
                lastName
                email
                imageUrl
                role
                meetplaceId
                isConfirmedEmail
                isMeetplaceInvitationInvalid
                isMeetplaceInvitationExpired
                jobTitle
                userStatus
            }
        }
    }
`)

const useUsersFilter = (
    value: string,
    type = 'filter',
    excludedIds = [],
    excludeGuest = false,
    status?: string,
) => {
    const GUEST_ROLE_ID = excludeGuest
        ? 'e0306612-78a8-4636-8422-7eb1caa474f9'
        : ''

    const keyword = value || ''

    const [
        {
            data: filteredData,
            fetching: fetchingFilteredData,
            error: errorFilteredData,
        },
        getFilteredUsers,
    ] = useQuery({
        query: FILTER_USERS,
        variables: {
            where: {
                and: [
                    { id: { nin: excludedIds } },
                    {
                        or: [
                            { fullName: { contains: keyword } },
                            { email: { contains: keyword } },
                        ],
                    },
                    { role: { neq: GUEST_ROLE_ID } },
                    { userStatus: { neq: User_UserStatus.Deactivated } },
                    ...(status
                        ? [{ userStatus: { eq: status as User_UserStatus } }]
                        : []),
                ],
            },
            first: 15,
            order: { fullName: SortEnumType.Asc },
        },
        pause: true,
    })

    const [{ data, fetching, error }, getUsers] = useQuery({
        query: GET_USERS,
        variables: {
            order: { fullName: SortEnumType.Asc },
        },
        pause: true,
    })
    return type === 'filter'
        ? {
              getData: getFilteredUsers,
              data: filteredData,
              fetching: fetchingFilteredData,
              error: errorFilteredData,
          }
        : { getData: getUsers, data, fetching, error }
}

type UseFilterMeetplaceUsersArgs = {
    keyword: string
    pause: boolean
    where?: UserFilterInput
}

const useFilterMeetplaceUsers = ({
    keyword,
    pause,
    where,
}: UseFilterMeetplaceUsersArgs) => {
    const [{ data, fetching, error }] = useQuery({
        query: FILTER_USERS,
        variables: {
            where: {
                and: [
                    {
                        or: [
                            {
                                fullName: {
                                    contains: keyword,
                                },
                            },
                            { email: { contains: keyword } },
                        ],
                    },
                    { userStatus: { neq: User_UserStatus.Deactivated } },
                    ...(where ? [where] : []),
                ],
            },
            first: 15,
        },
        pause,
    })

    return {
        users: data?.users?.nodes ?? [],
        fetching,
        error,
    }
}

const useUser = () => {
    const { status } = useSession()

    const [{ data, error, fetching }, refetch] = useQuery({
        query: GET_USER_DATA,
        context: useMemo(() => ({ suspense: true }), []),
        pause: status !== 'authenticated',
    })

    const isGuestUser = useMemo(
        () => !data?.me?.isConfirmedEmail,
        [data?.me?.isConfirmedEmail],
    )

    const userRoleKey = useMemo(
        () => userRolesFromId[data?.me?.role],
        [data?.me?.role],
    )

    const signOut = useCallback(async (navigateTo = '/signin') => {
        nextAuthSignOut({
            callbackUrl: `${window.location.origin}${navigateTo}`,
        })
        await clearPreferences()
    }, [])

    return {
        user: data?.me,
        error,
        fetching,
        isGuestUser,
        userRoleKey,
        status,
        signOut,
        refetch,
    }
}

export {
    GET_USER_DATA,
    GET_USER,
    CREATE_NON_CONFIRMED_USER,
    useUsersFilter,
    useFilterMeetplaceUsers,
    useUser,
}
